import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  AccountInfo,
  AuthenticationResult,
  PublicClientApplication,
} from '@azure/msal-browser';
import { defer, Observable, throwError } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';

import { environment } from '../../environments/environment';
import {
  ApiResponse,
  AuthConfig,
  LoginData,
  UserInfo,
} from '../types/auth.types';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly apiAuthUrl = environment.apiAuthUrl;
  private readonly systemId = environment.systemId;

  /**
   * Versión del esquema de sesión guardado en `localStorage`.
   *
   * Al incrementarla, las sesiones emitidas por versiones anteriores dejan de
   * ser legibles y el usuario vuelve al login. Se subió a `v2` al migrar
   * producción al microservicio de autenticación productivo y a la nueva
   * aplicación de Azure: `localStorage` sobrevive a los despliegues, así que
   * los tokens anteriores seguían enviándose a la API, que los aceptaba sin
   * los roles esperados y respondía 403.
   */
  private static readonly SESSION_VERSION = 'v2';

  /**
   * Claves usadas por versiones anteriores del esquema de sesión.
   * Se eliminan al arrancar; ver {@link purgeLegacySession}.
   */
  private static readonly LEGACY_SESSION_KEYS = [
    'userInfo',
    'jwtToken',
    'jwtRefreshToken',
  ];

  private readonly userInfoKey = `userInfo.${AuthService.SESSION_VERSION}`;
  private readonly accessTokenKey = `jwtToken.${AuthService.SESSION_VERSION}`;
  private readonly refreshTokenKey = `jwtRefreshToken.${AuthService.SESSION_VERSION}`;

  private config: AuthConfig = {
    clientId: environment.azure.clientId,
    authority: `https://login.microsoftonline.com/${environment.azure.tenantId}`,
    redirectUri: environment.azure.redirectUri,
    scopes: environment.azure.scopes,
  };

  private msalInstance: PublicClientApplication;
  private initialized = false;

  /**
   * Observable compartido para evitar múltiples llamados concurrentes al refresh.
   */
  private refreshInFlight$: Observable<string> | null = null;

  public user = signal<AccountInfo | null>(null);
  public userInfo = signal<UserInfo | null>(null);
  private userRoles = signal<string[]>([]);
  public authError = signal<string | null>(null);
  public isSigningIn = signal<boolean>(false);

  constructor(
    private router: Router,
    private http: HttpClient,
  ) {
    this.purgeLegacySession();

    this.msalInstance = new PublicClientApplication({
      auth: {
        clientId: this.config.clientId,
        authority: this.config.authority,
        redirectUri: this.config.redirectUri,
        navigateToLoginRequestUrl: true,
      },
      cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: true,
        secureCookies: true,
      },
      system: {
        allowRedirectInIframe: true,
        windowHashTimeout: 60000,
        iframeHashTimeout: 6000,
      },
    });

    this.msalInstance.initialize().then(() => {
      this.initialized = true;
      this.initializeAuth();
    });
  }

  private initializeAuth(): void {
    defer(() => this.msalInstance.handleRedirectPromise())
      .pipe(
        tap((result) => {
          if (result) {
            this.handleAuthenticationSuccess(result);
          } else {
            const accounts = this.msalInstance.getAllAccounts();
            if (accounts.length > 0) {
              this.user.set(accounts[0]);
              this.tapUserInfo();

              if (this.router.url.includes('/auth/login')) {
                this.router.navigate(['/app/home']);
              }
            } else {
              this.router.navigate(['/auth/login']);
            }
          }
        }),
        catchError((error) => {
          console.error('Error durante la autenticación:', error);
          this.router.navigate(['/auth/login']);
          return defer(async () => null);
        }),
      )
      .subscribe();
  }

  public login(): Observable<void> {
    if (!this.initialized) {
      return throwError(() => new Error('MSAL no se ha inicializado aún'));
    }

    return defer(() => {
      this.msalInstance.loginRedirect({ scopes: this.config.scopes });
      return Promise.resolve();
    });
  }

  public logout(): Observable<void> {
    const info = this.getUserInfo() as unknown as { userId?: string } | null;
    const userId = info?.userId ?? '';
    const systemId = this.systemId;

    return this.http
      .post<ApiResponse<string>>(`${this.apiAuthUrl}/auth/logout`, {
        userId,
        systemId,
      })
      .pipe(
        tap({
          next: (resp) => {
            if (!resp?.isSuccess) {
              console.warn(`Logout backend no exitoso: ${resp?.message}`);
            }
            this.user.set(null);
            this.userInfo.set(null);
            this.userRoles.set([]);
            localStorage.removeItem(this.userInfoKey);
            localStorage.removeItem(this.accessTokenKey);
            localStorage.removeItem(this.refreshTokenKey);
          },
        }),
        switchMap(() =>
          defer(() => {
            this.msalInstance.logoutRedirect().catch(() => {});
            return Promise.resolve<void>(undefined);
          }),
        ),
        catchError((error) => {
          console.error('Error durante el logout:', error);
          this.router.navigate(['/auth/login']);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Elimina las sesiones guardadas por versiones anteriores de la aplicación.
   *
   * `localStorage` no se limpia al desplegar: sin esta purga, un token emitido
   * antes de cambiar de microservicio de autenticación se seguiría enviando a
   * la API. No basta con limpiar la sesión en el login, porque `authGuard`
   * deja pasar al área protegida sin pasar por esa pantalla.
   */
  private purgeLegacySession(): void {
    for (const key of AuthService.LEGACY_SESSION_KEYS) {
      localStorage.removeItem(key);
    }
  }

  public clearSession(): void {
    this.user.set(null);
    this.userInfo.set(null);
    this.userRoles.set([]);
    localStorage.removeItem(this.userInfoKey);
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
  }

  public isAuthenticated(): boolean {
    const hasUserInfo = this.getUserInfo() !== null;
    const hasMsalAccounts = this.msalInstance.getAllAccounts().length > 0;
    return hasMsalAccounts && hasUserInfo;
  }

  public getUserInfo(): UserInfo | null {
    try {
      const storedData = localStorage.getItem(this.userInfoKey);
      if (!storedData) return null;

      const parsedData = JSON.parse(storedData);
      if (typeof parsedData !== 'object' || parsedData === null) return null;

      return parsedData as UserInfo;
    } catch (error) {
      console.error('Error al recuperar información de usuario:', error);
      return null;
    }
  }

  public setUserInfo(userInfo: unknown): void {
    localStorage.setItem(this.userInfoKey, JSON.stringify(userInfo));
  }

  public getAccessToken(): string | null {
    return localStorage.getItem(this.accessTokenKey);
  }

  public setAccessToken(token: string): void {
    localStorage.setItem(this.accessTokenKey, token);
  }

  public getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  public setRefreshToken(token: string): void {
    localStorage.setItem(this.refreshTokenKey, token);
  }

  public handleRefreshToken(expiredToken: string): Observable<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No existe Refresh Token almacenado'));
    }

    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const request$ = this.http
      .post<ApiResponse<LoginData>>(`${this.apiAuthUrl}/auth/refresh`, {
        accessToken: expiredToken || '',
        refreshToken,
      })
      .pipe(
        switchMap((response) => {
          if (!response?.isSuccess || !response?.data) {
            const msg =
              response?.message || 'Refresh token inválido o expirado';
            return throwError(() => new Error(msg));
          }
          this.setAccessToken(response.data.token);
          this.setRefreshToken(response.data.refreshToken);
          return defer(() => Promise.resolve(response.data.token));
        }),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

    this.refreshInFlight$ = request$;
    return request$;
  }

  public isTokenExpiringSoon(token: string): boolean {
    const decoded = this.safeDecodeJWT(token);

    if (!decoded || !decoded.exp) {
      return true;
    }

    const nowInSec = Math.floor(Date.now() / 1000);
    const secondsLeft = decoded.exp - nowInSec;
    const REFRESH_MARGIN = 30;

    return secondsLeft <= REFRESH_MARGIN;
  }

  private handleAuthenticationSuccess(result: AuthenticationResult): void {
    if (!result.account) return;

    this.user.set(result.account);

    const idTokenClaims = result.idTokenClaims as any;
    const name = idTokenClaims.name || idTokenClaims.given_name || '';
    const email =
      idTokenClaims.preferred_username || idTokenClaims.emails?.[0] || '';

    defer(() =>
      this.msalInstance.acquireTokenSilent({
        scopes: this.config.scopes,
        account: result.account,
      }),
    )
      .pipe(
        switchMap((tokenResponse) => {
          this.authError.set(null);
          this.isSigningIn.set(true);

          return this.http
            .post<ApiResponse<LoginData>>(`${this.apiAuthUrl}/auth/login`, {
              tipo: 'Ms365',
              token: tokenResponse.accessToken,
              idSistema: this.systemId,
            })
            .pipe(
              tap((response) => {
                if (!response.isSuccess || !response.data) {
                  const errorMessage =
                    response.message ||
                    'Error al validar credenciales durante el inicio de sesión';
                  this.authError.set(errorMessage);
                  this.user.set(null);
                  this.userInfo.set(null);
                  this.userRoles.set([]);
                  this.isSigningIn.set(false);
                  this.router.navigate(['/auth/login']);
                  throw new Error(errorMessage);
                }

                const { token, refreshToken, profile } = response.data;

                this.setAccessToken(token);
                this.setRefreshToken(refreshToken);

                let roles: string[] = Array.isArray(profile?.roles)
                  ? (profile!.roles as string[])
                  : [];
                if (!roles || roles.length === 0) {
                  try {
                    const tokenPayload = this.safeDecodeJWT(token);
                    roles = tokenPayload?.roles || [];
                  } catch {
                    roles = [];
                  }
                }

                this.userRoles.set(roles);

                const userInfo: UserInfo = {
                  userId:
                    profile?.idUsuario !== undefined
                      ? String(profile.idUsuario)
                      : undefined,
                  name: profile?.nombreUsuario || name,
                  email: profile?.email || email,
                  employeeNumber: profile?.numeroEmpleado
                    ? String(profile.numeroEmpleado)
                    : undefined,
                  roles,
                };

                this.setUserInfo(userInfo);
                this.tapUserInfo();

                this.isSigningIn.set(false);
                this.router.navigate(['/app/home']);
              }),
              catchError((error) => {
                // Deja rastro del error real (status 0 = red/CORS/bloqueo en el cliente,
                // 4XX/5XX = respuesta del servidor) para Inspectlet y la consola, ya que
                // al usuario sólo se le muestra el mensaje genérico.
                console.error('[auth/login] Falló el inicio de sesión', {
                  status: error?.status,
                  statusText: error?.statusText,
                  url: error?.url,
                  message: error?.error?.message ?? error?.message,
                  online: navigator.onLine,
                });
                const errorMessage =
                  'Error al validar token durante el inicio de sesión';
                this.authError.set(errorMessage);
                this.user.set(null);
                this.userInfo.set(null);
                this.userRoles.set([]);
                this.isSigningIn.set(false);
                this.router.navigate(['/auth/login']);
                return throwError(() => new Error(errorMessage));
              }),
            );
        }),
        catchError((error) => {
          console.error('Error durante el proceso de autenticación:', error);
          this.userRoles.set([]);
          return defer(async () => null);
        }),
      )
      .subscribe();
  }

  private tapUserInfo(): void {
    const userInfo = this.getUserInfo();
    if (userInfo) {
      this.userInfo.set(userInfo);
      this.userRoles.set(userInfo.roles ?? []);
    }
  }

  public hasRole(role: string): boolean {
    return this.userRoles().includes(role);
  }

  public getTokenUserInfo(token: string): {
    email?: string;
    userId?: string;
    numeroEmpleado?: string;
    exp?: number;
  } | null {
    const payload = this.safeDecodeJWT(token);
    if (!payload) return null;

    return {
      email: payload.email,
      userId: payload.userId,
      numeroEmpleado: payload.numeroEmpleado,
      exp: payload.exp,
    };
  }

  private safeDecodeJWT(token: string): any | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const s = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = s + '='.repeat((4 - (s.length % 4)) % 4);
      return JSON.parse(atob(padded));
    } catch {
      return null;
    }
  }

  private extractRolesFromToken(tokenPayload: any): string[] {
    if (!tokenPayload) return [];

    let roles =
      tokenPayload.roles ||
      tokenPayload[
        'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'
      ] ||
      [];

    if (typeof roles === 'string') {
      roles = [roles];
    }

    return Array.isArray(roles) ? roles : [];
  }
}
